import { Component } from '@theme/component';
import { morphSection, normalizeSectionId } from '@theme/section-renderer';

/**
 * A custom element that submits the newsletter signup form over fetch and
 * morphs the result into place, instead of navigating to /contact.
 *
 * @typedef {object} EmailSignupFormRefs
 * @property {HTMLFormElement} form
 * @extends Component<EmailSignupFormRefs>
 */
class EmailSignupForm extends Component {
  requiredRefs = ['form'];

  /** @param {SubmitEvent} event */
  handleSubmit(event) {
    event.preventDefault();
    // hCaptcha auto-wires its own 'submit' listener directly on the form as
    // soon as the visitor interacts with it (e.g. focuses the email field).
    // Without this, that listener still receives this same event after ours
    // and independently does a real native submit once its own challenge
    // resolves — causing a second, page-reloading submission alongside ours.
    event.stopImmediatePropagation();

    const { form } = this.refs;
    if (!form.reportValidity()) return;

    const sectionElement = this.closest('.shopify-section');

    // No section wrapper to morph into (e.g. rendered outside the normal
    // section flow) — fall back to a normal form submission.
    if (!sectionElement) {
      form.submit();
      return;
    }

    const submit = () => this.#submitViaFetch(form, sectionElement);

    // Shopify's hCaptcha only auto-wires to a form on user interaction (e.g.
    // focusing the email field) and injects its token just before a native
    // submit. Since we submit programmatically via fetch, we must explicitly
    // wire it up first so the token exists on the FormData we read.
    // https://shopify.dev/docs/storefronts/themes/trust-security/captcha
    if (window.Shopify?.captcha?.protect) {
      window.Shopify.captcha.protect(form, submit);
    } else {
      submit();
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {Element} sectionElement
   */
  async #submitViaFetch(form, sectionElement) {
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
      });

      const html = await response.text();
      await morphSection(normalizeSectionId(sectionElement.id), html);

      /** @type {HTMLElement | null} */ (this.querySelector('.email-signup__message'))?.focus();
    } catch (error) {
      console.error(error);
      form.submit();
    }
  }
}

if (!customElements.get('email-signup-form')) {
  customElements.define('email-signup-form', EmailSignupForm);
}